/**
 * ApprovalCardHeader component.
 *
 * Displays icon, display_name, breadcrumb, timestamps, and ownership information.
 */

import type { CSSProperties } from 'react'
import {
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/shared/components/ui'
import { getToolIcon } from '../../utils/icons'
import { formatRelativeTime } from '../../utils/time'
import type { ApprovalItem } from '../../types'

export interface ApprovalCardHeaderProps {
  /** The approval item metadata. */
  item: ApprovalItem
  /** The display configuration from the registry. */
  display: {
    name: string
    icon: string
    color: string
  }
  /**
   * Human-readable name of the app this approval acts on (e.g. "My
   * Timetable"). Rendered as a prominent chip on the header's bottom row so
   * the operator sees which app the action targets at a glance. Absent for
   * tools that don't act on a specific app.
   */
  appLabel?: string | null
  /** The current operator's ID, for determining ownership. */
  currentOperatorId?: string
  /** Called when release is clicked. */
  onRelease?: () => void
  /** Whether release is pending. */
  releasePending?: boolean
  /**
   * Outcome of the execution summary (e.g. `pending_review`, `success`,
   * `failed`). Surfaced as a status chip alongside the relative
   * timestamp so the headline state is visible without scrolling into
   * the body. Sourced upstream from `execution_summary.outcome`.
   */
  outcome?: string | null
}

export function ApprovalCardHeader({
  item,
  display,
  appLabel,
  currentOperatorId,
  onRelease,
  releasePending,
  outcome,
}: ApprovalCardHeaderProps) {
  const Icon = getToolIcon(display.icon)
  const isOwnedByMe = item.owner_id === currentOperatorId

  return (
    <TooltipProvider>
      <header style={styles.header}>
        {/* Top row: Icon + Name on the left; all metadata grouped on the right.
            The right-side stack collapses timestamp / ownership / action icons
            onto one row so the header is two compact rows rather than three —
            previously the timestamp, the "Unclaimed" pill, and the icon
            buttons sat on three separate lines that fought for attention. */}
        <div style={styles.topRow}>
          <div style={styles.titleGroup}>
            {/* Tool icon with color accent */}
            <div
              style={{
                ...styles.iconContainer,
                backgroundColor: `${display.color}20`,
              }}
            >
              <Icon
                style={{ ...iconStyles.icon16, color: display.color }}
                aria-hidden="true"
              />
            </div>

            {/* Display name. Sentence-cased rather than all-caps so it
                reads as a heading instead of a shout — the icon already
                signals "this is the tool". */}
            <h2 style={styles.displayName}>
              {formatDisplayName(display.name)}
            </h2>
          </div>

          {/* Right side: outcome + timestamp + ownership + actions, on one row */}
          <div style={styles.actions}>
            {/* Outcome status chip. Lifted from the execution summary
                body so the headline state ("Pending review", "Success",
                "Failed") sits where the eye lands first rather than two
                scroll positions down. */}
            {outcome && <OutcomeChip outcome={outcome} />}

            {/* Relative time with tooltip showing absolute */}
            <Tooltip>
              <TooltipTrigger asChild>
                <time
                  dateTime={item.created_at}
                  style={styles.timestamp}
                >
                  {formatRelativeTime(item.created_at)}
                </time>
              </TooltipTrigger>
              <TooltipContent>
                {new Date(item.created_at).toLocaleString()}
              </TooltipContent>
            </Tooltip>

            {/* Ownership indicator — promoted into the top row so it sits
                alongside the timestamp, freeing the row that previously
                only held the breadcrumb. */}
            <OwnershipBadge ownerId={item.owner_id} isOwnedByMe={isOwnedByMe} />

            {/* Release button (only shown when owned by current operator).
                The "View agent session" icon and the more-actions
                dropdown that used to sit here have been promoted to
                the SidePanel's top header bar (see
                ApprovalHeaderActions) so all chrome controls share
                one row alongside maximize/close. */}
            {isOwnedByMe && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onRelease}
                disabled={releasePending || !onRelease}
              >
                {releasePending ? 'Releasing...' : 'Release'}
              </Button>
            )}
          </div>
        </div>

        {/* Bottom row: app chip + breadcrumb. The app chip leads the line so
            "which app does this act on?" is answered right under the tool
            name. Ownership was promoted into the top row, so this line stays
            calm and dedicated to identity context. Rendered whenever either
            the app label or breadcrumb is present. */}
        {(appLabel || item.breadcrumb) && (
          <div style={styles.bottomRow}>
            {appLabel && (
              <Badge
                variant="outline"
                style={appBadgeStyle}
                data-testid="approval-app-badge"
              >
                {appLabel}
              </Badge>
            )}
            {item.breadcrumb && (
              <p style={styles.breadcrumb}>{item.breadcrumb}</p>
            )}
          </div>
        )}
      </header>
    </TooltipProvider>
  )
}

/**
 * Outcome status chip. Maps the raw outcome token (e.g.
 * `pending_review`) to a human-readable label and a semantic colour
 * accent. Unknown outcomes fall back to a neutral chip — we'd rather
 * surface an unfamiliar label than swallow the signal.
 */
interface OutcomeChipProps {
  outcome: string
}

function OutcomeChip({ outcome }: OutcomeChipProps) {
  const normalised = outcome.trim().toLowerCase().replace(/[\s-]+/g, '_')
  const palette = outcomePalette[normalised] ?? outcomePalette.default
  return (
    <Badge
      variant="outline"
      style={{ ...ownershipBadgeStyles.chip, ...palette }}
    >
      {humaniseOutcome(outcome)}
    </Badge>
  )
}

function humaniseOutcome(raw: string): string {
  const cleaned = raw.replace(/[_\-.]+/g, ' ').trim()
  if (!cleaned) return raw
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
}

const outcomePalette: Record<string, CSSProperties> = {
  pending_review: {
    color: 'var(--color-warning, #c8801a)',
    background: 'var(--color-warning-bg, rgba(200, 128, 26, 0.10))',
    borderColor: 'var(--color-warning-border, rgba(200, 128, 26, 0.35))',
  },
  pending: {
    color: 'var(--color-warning, #c8801a)',
    background: 'var(--color-warning-bg, rgba(200, 128, 26, 0.10))',
    borderColor: 'var(--color-warning-border, rgba(200, 128, 26, 0.35))',
  },
  success: {
    color: 'var(--success, #1f9d55)',
    background: 'rgba(31, 157, 85, 0.10)',
    borderColor: 'rgba(31, 157, 85, 0.35)',
  },
  succeeded: {
    color: 'var(--success, #1f9d55)',
    background: 'rgba(31, 157, 85, 0.10)',
    borderColor: 'rgba(31, 157, 85, 0.35)',
  },
  failed: {
    color: 'var(--error, #d9534f)',
    background: 'rgba(217, 83, 79, 0.10)',
    borderColor: 'rgba(217, 83, 79, 0.35)',
  },
  cancelled: {
    color: 'var(--text-muted)',
    background: 'var(--bg-tertiary)',
    borderColor: 'var(--border-subtle)',
  },
  default: {
    color: 'var(--text-secondary, var(--text-muted))',
    background: 'var(--bg-tertiary)',
    borderColor: 'var(--border-subtle)',
  },
}

interface OwnershipBadgeProps {
  ownerId: string | null
  isOwnedByMe: boolean
}

function OwnershipBadge({ ownerId, isOwnedByMe }: OwnershipBadgeProps) {
  if (!ownerId) {
    return (
      <Badge variant="outline" style={ownershipBadgeStyles.unclaimed}>
        Unclaimed
      </Badge>
    )
  }

  if (isOwnedByMe) {
    return (
      <Badge variant="primary" style={ownershipBadgeStyles.chip}>
        Claimed by you
      </Badge>
    )
  }

  return (
    <Badge variant="default" style={ownershipBadgeStyles.chip}>
      Claimed by: {ownerId}
    </Badge>
  )
}

/**
 * Convert a backend display name (often delivered as `SEND EMAIL`,
 * `send_email`, or `Send Email`) into the sentence-cased label the UI
 * shows. Keeps acronyms intact when they arrive as multi-letter tokens
 * (e.g. `IMAP`, `URL`).
 */
function formatDisplayName(name: string): string {
  if (!name) return name
  // If the backend already mixed case (e.g. "Send Email") trust it.
  if (/[a-z]/.test(name) && /[A-Z]/.test(name)) return name
  return name
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) =>
      word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ')
}

/**
 * App chip on the header's bottom row. Accent-coloured so it reads as the
 * primary identity context (which app) rather than a neutral metadata pill.
 */
const appBadgeStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.01em',
  textTransform: 'none',
  flexShrink: 0,
  color: 'var(--accent-primary)',
  borderColor: 'var(--accent-primary)',
}

const ownershipBadgeStyles: Record<string, CSSProperties> = {
  unclaimed: {
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.01em',
    textTransform: 'none',
    color: 'var(--text-muted)',
    background: 'var(--bg-tertiary)',
    borderColor: 'var(--border-subtle)',
  },
  chip: {
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.01em',
    textTransform: 'none',
  },
}

const iconStyles: Record<string, CSSProperties> = {
  icon16: {
    width: '16px',
    height: '16px',
  },
}

const styles: Record<string, CSSProperties> = {
  // NOTE: paddingTop is carried HERE rather than on the parent article
  // because the article's sticky action bar lives just below the
  // header; if the article had its own padding-top, that padding would
  // sit between the scrollport edge and the sticky bar's `top: 0`
  // anchor, letting body content scroll into the gap before the bar
  // sticks. Keeping breathing room inside the header keeps the visual
  // layout identical at scroll=0 while letting the sticky bar reach
  // the true top of the scrollport.
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    borderBottom: '1px solid var(--border-default)',
    paddingTop: '18px',
    paddingBottom: '12px',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minWidth: 0,
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
    flex: '1 1 auto',
  },
  iconContainer: {
    display: 'flex',
    width: '28px',
    height: '28px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-md)',
    flexShrink: 0,
  },
  displayName: {
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '-0.005em',
    color: 'var(--text-primary)',
    wordBreak: 'break-word' as const,
    margin: 0,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  timestamp: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  breadcrumb: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    wordBreak: 'break-word' as const,
    margin: 0,
    minWidth: 0,
  },
}

export default ApprovalCardHeader
